<template>
    <ul
        :key="`selected-tab_${selectedTabId}`"
        class="a-tabs"
        :class="[
            { 'bottom-border': bottomBorder },
            { 'button-tabs': button },
            { 'icon-only': iconOnly },
            direction,
        ]"
    >
        <li
            v-for="(tab, index) in reactiveTabs"
            :key="`tab_${tab.id}`"
            v-tooltip="{
                content: tab.disabledText ? tab.disabledText : '',
                delay: { show: 500, hide: 100 },
            }"
            :class="[
                { selected: isTab(tab.id) },
                { 'disabled-tab': tab.disabled },
                { 'px-4': iconOnly },
                { 'px-5': !iconOnly },
                'py-2',
                'tab',
            ]"
            @click="onTab(tab)"
        >
            <p v-if="numbered && !button" class="numbered-item mr-2">
                <span>{{ index + 1 }}</span>
            </p>
            <template v-if="tab.tabIcon">
                <font-awesome-icon
                    :icon="`${iconOnly ? 'fas' : 'fal'} ${tab.tabIcon}`"
                    :class="['icon', { 'mr-2': !iconOnly }]"
                />
            </template>
            <p v-if="!iconOnly" class="grey02--text">
                {{ tab.text }}
            </p>
            <template v-if="tab.appendIcon && !iconOnly">
                <font-awesome-icon
                    :icon="`fal ${tab.appendIcon}`"
                    class="icon ml-2"
                />
            </template>
            <p
                v-if="tab.appendCount && !button"
                class="count-item ml-2 peacock"
            >
                {{ tab.appendCount }}
            </p>
        </li>
    </ul>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    tabs: {
        type: Array,
        required: true,
    },
    currentTab: {
        type: [Number, String],
        default: null,
    },
    bottomBorder: {
        type: Boolean,
        default: false,
    },
    numbered: {
        type: Boolean,
        default: false,
    },
    button: {
        type: Boolean,
        default: false,
    },
    direction: {
        type: String,
        default: 'horiz',
        validator: (v) => ['horiz', 'vert'].includes(v),
    },
    overWriter: {
        type: Boolean,
        default: false,
    },
    matchingStateProp: {
        type: String,
        default: null,
    },
    appendCount: {
        type: [Number, String],
        default: null,
    },
    iconOnly: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['tab-change']);

const reactiveTabs = ref(props.tabs);
const selectedTabId = ref(
    props.currentTab ? props.currentTab : props.tabs[0].id
);

const currentStep = computed(() => {
    if (this.$store) {
        return this.$store.state.app[props.matchingStateProp]
            ? this.$store.state.app[props.matchingStateProp]
            : 1;
    } else {
        return 1;
    }
});

watch(
    () => props.tabs,
    (newVal) => {
        reactiveTabs.value = newVal;
    }
);

watch(
    () => props.currentTab,
    (newVal) => {
        selectedTabId.value = newVal;
    }
);

const isTab = (id) => {
    if (props.overWriter) {
        return currentStep.value
            ? currentStep.value === id
            : selectedTabId.value === id;
    } else {
        return selectedTabId.value === id;
    }
};

const onTab = (tab) => {
    if (tab.disabled) return;
    selectedTabId.value = tab.id;
    emit('tab-change', tab);
};
</script>

<script>
export default {
    name: 'AonTabs',
};
</script>

<style lang="scss">
.a-tabs {
    width: 100%;
    display: flex;

    position: relative;
    z-index: 0;

    &.centered {
        justify-content: center;
    }

    &.bottom-border {
        border-bottom: 1px solid $grey05;
    }

    &.vert {
        flex-direction: column;
        justify-content: flex-start;
        margin-top: 20px;
        width: auto;

        li {
            border-left: 4px solid transparent;
            border-bottom: none;
            justify-content: flex-start;
            height: 40px;

            &.selected {
                border-left: 4px solid $peacock;
                border-bottom: none;
            }
        }
    }

    .icon {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        font-size: 20px;

        &:before {
            font-size: 18px;
        }
    }

    li {
        font-size: 14px;
        border-bottom: 4px solid transparent;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        min-width: 130px;

        transition: all 0.3s ease-in-out;

        p {
            font-weight: bold;
            text-transform: uppercase;
            color: $grey02;
        }

        svg {
            color: $grey02;
        }

        &:hover {
            border-color: $grey02;
            p {
                color: $grey02;
            }
        }

        &.selected {
            border-bottom: 4px solid $peacock;

            .numbered-item,
            p {
                color: $peacock;
                font-weight: 600;

                &:after {
                    background: $peacock !important;
                }
            }

            .icon {
                color: $peacock;
            }
        }
        &.disabled-tab {
            opacity: 0.2;
            cursor: default;
            border-color: transparent;
            pointer-events: none;
        }

        .count-item {
            font-size: 12px;
            font-weight: 600;
            width: auto;
            position: relative;
            border-radius: 100px;
            padding: 0px 10px 0px 10px;
            color: white !important;

            &:before {
                border-radius: 50%;
                content: '';
                height: 10px;
                left: 6px;
                margin-top: -5px;
                position: absolute;
                top: 50%;
                width: 10px;
            }
        }
    }

    &.button-tabs {
        width: auto;
        border-bottom: none;

        li {
            border-right: 1px solid $peacock;
            border-bottom: 1px solid $peacock;
            border-top: 1px solid $peacock;

            padding-top: 12px !important;
            padding-bottom: 12px !important;

            transition: all 0.3s ease-in-out;

            &:first-of-type {
                border-radius: 4px 0 0 4px;
                border-left: 1px solid $peacock;
            }

            &:last-of-type {
                border-radius: 0 4px 4px 0;
            }

            &.selected {
                border-bottom: none;
                background: $peacock;

                p {
                    color: white;
                }

                .icon {
                    color: white;
                }
            }

            &:hover {
                background: $peacockHover;
                border-color: $peacockHover;

                p {
                    color: white;
                    text-decoration: underline;
                }

                .icon {
                    color: white;
                }
            }

            &:active {
                background: $peacockActive;
                border-color: $peacockActive;

                p {
                    color: white;
                    text-decoration: underline;
                }

                .icon {
                    color: white;
                }
            }

            p {
                font-weight: normal;
                text-transform: none;
                color: $peacock;

                transition: all 0.3s ease-in-out;
            }
        }
    }

    &.icon-only {
        li {
            min-width: 50px;
        }
    }
}
</style>
